import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {useEffect, useRef} from 'react';
import {useAuth} from '@api/auth';
import {IS_MOCK} from '@api/mock-mode';
import {useActiveWorkspace, useMyAccess} from '@api/workspaces';
import type {
  AnalyticsInsightInclude,
  AnalyticsInsightMyStatePatch,
  AnalyticsInsightsRequest,
} from '@api/analytics-insights';
import {isAnalyticsInsightStateConflict} from '@api/analytics-insights';
import {weeklyReportsClient} from './client';

/** Ключи персонального кэша недельных отчётов. */
export const weeklyReportKeys = {
  list: (workspaceSid: string | null, actorSid: string | null) =>
    ['weekly-reports', actorSid, workspaceSid, 'list'] as const,
  detail: (
    workspaceSid: string | null,
    actorSid: string | null,
    reportSid: string | null,
  ) => ['weekly-reports', actorSid, workspaceSid, 'detail', reportSid] as const,
  insights: (
    workspaceSid: string | null,
    actorSid: string | null,
    reportSid: string | null,
    request: AnalyticsInsightsRequest,
  ) =>
    [
      'weekly-reports',
      actorSid,
      workspaceSid,
      reportSid,
      'insights',
      request,
    ] as const,
  insight: (
    workspaceSid: string | null,
    actorSid: string | null,
    reportSid: string | null,
    insightSid: string | null,
    include: AnalyticsInsightInclude[],
  ) =>
    [
      'weekly-reports',
      actorSid,
      workspaceSid,
      reportSid,
      'insight',
      insightSid,
      include,
    ] as const,
};

const useReportIdentity = () => {
  const {activeId} = useActiveWorkspace();
  const {currentUser} = useAuth();
  const actorSid = currentUser?.sid ?? (IS_MOCK ? 'mock-user' : null);
  const access = useMyAccess(activeId);
  const accessSignature = JSON.stringify(access.data ?? null);
  const previousAccess = useRef(accessSignature);
  const queryClient = useQueryClient();
  useEffect(() => {
    queryClient.removeQueries({
      predicate: (query) =>
        query.queryKey[0] === 'weekly-reports' &&
        (query.queryKey[1] !== actorSid || query.queryKey[2] !== activeId),
    });
  }, [actorSid, activeId, queryClient]);
  useEffect(() => {
    if (previousAccess.current !== accessSignature) {
      previousAccess.current = accessSignature;
      void queryClient.resetQueries({
        queryKey: ['weekly-reports', actorSid, activeId],
      });
    }
  }, [accessSignature, actorSid, activeId, queryClient]);
  return {activeId, actorSid};
};

/** Список отчётов текущего пользователя с серверной cursor-пагинацией. */
export const useWeeklyReports = () => {
  const {activeId, actorSid} = useReportIdentity();
  return useInfiniteQuery({
    queryKey: weeklyReportKeys.list(activeId, actorSid),
    queryFn: ({pageParam}) =>
      weeklyReportsClient.list(activeId as string, pageParam ?? undefined),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: !IS_MOCK && Boolean(activeId) && Boolean(actorSid),
    gcTime: 0,
  });
};

/** Детальный отчёт с повторной серверной проверкой доступа при открытии. */
export const useWeeklyReport = (reportSid: string | null) => {
  const {activeId, actorSid} = useReportIdentity();
  return useQuery({
    queryKey: weeklyReportKeys.detail(activeId, actorSid, reportSid),
    queryFn: () =>
      weeklyReportsClient.get(activeId as string, reportSid as string),
    enabled:
      !IS_MOCK && Boolean(activeId) && Boolean(actorSid) && Boolean(reportSid),
    staleTime: 0,
    refetchOnWindowFocus: true,
    gcTime: 0,
  });
};

/** Exact-manifest insight rows; actor/report/workspace have separate cache identities. */
export const useWeeklyReportInsights = (
  reportSid: string | null,
  request: Omit<AnalyticsInsightsRequest, 'cursor'>,
  options: {enabled?: boolean} = {},
) => {
  const {activeId, actorSid} = useReportIdentity();
  const queryClient = useQueryClient();
  const queryKey = weeklyReportKeys.insights(
    activeId,
    actorSid,
    reportSid,
    request,
  );
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({pageParam}) =>
      weeklyReportsClient.insights(activeId as string, reportSid as string, {
        ...request,
        cursor: pageParam,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.cursor ?? undefined,
    enabled:
      !IS_MOCK &&
      Boolean(activeId && actorSid && reportSid) &&
      (options.enabled ?? true),
    staleTime: 0,
    refetchOnWindowFocus: true,
    gcTime: 0,
  });
  const rows = query.isError
    ? []
    : (query.data?.pages.flatMap((page) => page.rows) ?? []);
  const refresh = () => queryClient.resetQueries({queryKey, exact: true});
  return {...query, rows, refresh};
};

export const useWeeklyReportInsight = (
  reportSid: string | null,
  insightSid: string | null,
  include: AnalyticsInsightInclude[] = [
    'enrichment',
    'tasks',
    'run',
    'my-state',
  ],
) => {
  const {activeId, actorSid} = useReportIdentity();
  return useQuery({
    queryKey: weeklyReportKeys.insight(
      activeId,
      actorSid,
      reportSid,
      insightSid,
      include,
    ),
    queryFn: () =>
      weeklyReportsClient.insight(
        activeId as string,
        reportSid as string,
        insightSid as string,
        include,
      ),
    enabled:
      !IS_MOCK && Boolean(activeId && actorSid && reportSid && insightSid),
    staleTime: 0,
    gcTime: 0,
  });
};

export const useUpdateWeeklyReportInsightMyState = (
  reportSid: string | null,
) => {
  const {activeId, actorSid} = useReportIdentity();
  const queryClient = useQueryClient();
  const refresh = () =>
    queryClient.resetQueries({
      queryKey: ['weekly-reports', actorSid, activeId, reportSid],
    });
  return useMutation({
    mutationFn: ({
      insightSid,
      dto,
    }: {
      insightSid: string;
      dto: AnalyticsInsightMyStatePatch;
    }) =>
      weeklyReportsClient.updateInsightMyState(
        activeId as string,
        reportSid as string,
        insightSid,
        dto,
      ),
    retry: false,
    onSuccess: refresh,
    onError: async (error) => {
      if (isAnalyticsInsightStateConflict(error)) await refresh();
    },
  });
};
